/**
 * Post-auth routing decision.
 *
 * Per `auth-admin-20260522/AUTH_FLOW.md §3`: after a successful
 * sign-in the host inspects `me.portals` and routes accordingly:
 *
 *   portals.length === 0  →  /no-access  (out-of-scope; placeholder
 *                             redirect for now — SCRUM-113
 *                             "out of scope" list)
 *   portals.length === 1  →  portalHome(portals[0].id)
 *   portals.length >  1   →  /portal     (chooser)
 *
 * This module exports two things:
 *
 * 1. `decidePostAuthRoute(portals)` — pure function. Easy to unit-
 *    test, easy to call from anywhere (route guard, login submit,
 *    chooser bootstrap).
 *
 * 2. `extractPortalEntitlements(session)` — safe extractor over the
 *    loosely-typed `useSession()` payload. The backend `/auth/me`
 *    does not yet emit `portals[]`; until it does the extractor
 *    returns `null` (distinct from `[]`) to signal "shape unknown,
 *    fall back to legacy behavior" rather than "user has 0 portals".
 *    The chooser/PortalPage treat `null` as "preserve current
 *    redirect-to-/ behavior" while `[]` properly routes to
 *    /no-access.
 */

import { isPortalId, portalHome, type PortalId } from './portal-id.js';

/**
 * One entry from `me.portals` per `AUTH_FLOW.md §2`.
 *
 * `context` is intentionally a loose `Record` here — the bundle's
 * full shape (`{ primary, secondary? }`) is enforced at the
 * `PortalChooser` render boundary, not here. The routing decision
 * itself only depends on `id`.
 */
export interface PortalEntitlement {
  readonly id: PortalId;
  readonly context?: {
    readonly primary: string;
    readonly secondary?: string;
  };
}

/**
 * The destination the host should navigate to after the user
 * successfully authenticates. Pure function — no side effects, no
 * hook plumbing — so it composes from a login submit handler,
 * a route guard, or a server-side render.
 *
 * `portals.length === 0` and the one-portal fast-path are both
 * driven by the bundle's spec. The chooser-route fallback handles
 * the multi-portal case.
 */
export function decidePostAuthRoute(
  portals: readonly PortalEntitlement[],
): string {
  if (portals.length === 0) return '/no-access';
  if (portals.length === 1) {
    const sole = portals[0];
    // The narrowing-guard pleases TS strict; the literal length===1
    // check already guarantees presence at runtime.
    if (sole === undefined) return '/no-access';
    // Single-portal fast-path → the bundle's canonical portal home.
    // NOTE: `/pms`, `/msa`, `/admin` are not registered in
    // ROUTE_MANIFEST yet, so a sole-PMS/MSA/Admin user would dead-end
    // on the 404 catch-all IF this branch were reachable today — it
    // isn't: the BE doesn't emit `me.portals[]` yet (SCRUM-128) and
    // `/login` doesn't route to `/portal`. SCRUM-198 re-introduces a
    // reachability guard (derived from ROUTE_MANIFEST) once the
    // portal-home routes land. Round-3 revert of the round-2 MEDIUM 3
    // guard, which hardcoded a set that falsely claimed `/admin` was
    // registered.
    return portalHome(sole.id);
  }
  return '/portal';
}

/**
 * Best-effort extractor over the loose `/auth/me` payload.
 *
 * Returns:
 *   - `PortalEntitlement[]` when the response carries a well-formed
 *     `portals` array (length 0+).
 *   - `null` when the response lacks `portals` (current backend
 *     behavior — the field is a soft-suggestion not yet shipped).
 *     Callers should treat `null` as "fall back to legacy
 *     redirect-to-/", NOT as "user has 0 portals".
 *
 * The narrowing is structural — we never assert types we cannot
 * verify at runtime. Anything that isn't a known `PortalId` in the
 * array is filtered out.
 */
export function extractPortalEntitlements(
  user: unknown,
): readonly PortalEntitlement[] | null {
  if (user === null || typeof user !== 'object') return null;
  const candidate = (user as { portals?: unknown }).portals;
  if (!Array.isArray(candidate)) return null;
  const out: PortalEntitlement[] = [];
  for (const entry of candidate) {
    if (entry === null || typeof entry !== 'object') continue;
    const id = (entry as { id?: unknown }).id;
    if (!isPortalId(id)) continue;
    const ctx = (entry as { context?: unknown }).context;
    if (
      ctx !== null &&
      typeof ctx === 'object' &&
      typeof (ctx as { primary?: unknown }).primary === 'string'
    ) {
      const primary = (ctx as { primary: string }).primary;
      const secondaryRaw = (ctx as { secondary?: unknown }).secondary;
      out.push({
        id,
        context: {
          primary,
          ...(typeof secondaryRaw === 'string'
            ? { secondary: secondaryRaw }
            : {}),
        },
      });
    } else {
      out.push({ id });
    }
  }
  return out;
}
